import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'

import type { SchemaQuota } from '@/__generated/es-api'
import Loader from '@/app/component/atom/loader'
import TabHeader from '@/app/component/atom/tab-header'
import CustomTooltip from '@/app/component/atom/tooltip'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import { useDeleteQuota } from '@/app/module/content/hooks/use-delete-quota'
import { useGetQuotas } from '@/app/module/content/hooks/use-get-quotas'

import QuotasTable from './table'

const Quotas: React.FC = () => {
  const createConfirmation = useConfirmation()
  const { data, isFetching, refetch } = useGetQuotas()
  const [deleteQuota, { isLoading: isDeleting }] = useDeleteQuota()

  const handleDeleteQuota = React.useCallback(
    async (quota: SchemaQuota) => {
      if (isDeleting || quota.campaigns > 0) {
        return
      }

      const confirmed = await createConfirmation({
        cancelButtonLabel: 'Cancel',
        confirmButtonLabel: 'Delete',
        content: (
          <Typography>
            Delete <Box component="strong">{quota.name}</Box> permanently? This cannot be undone.
          </Typography>
        ),
        icon: 'delete',
        title: 'Delete quota?',
      })

      if (confirmed) {
        await deleteQuota(quota.id)
      }
    },
    [isDeleting, createConfirmation, deleteQuota],
  )

  return (
    <Container>
      <TabHeader
        action={
          <CustomTooltip title="Reload Quotas">
            <IconButton onClick={refetch}>
              <Icon>refresh</Icon>
            </IconButton>
          </CustomTooltip>
        }
        title="Quotas"
      />
      <ContentContainer>
        <QuotasTable isDeleting={isDeleting} quotas={data?.quotas ?? []} onDeleteQuota={handleDeleteQuota} />
      </ContentContainer>
      {(isFetching || isDeleting) && <Loader />}
    </Container>
  )
}

const Container = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  maxHeight: '100%',
  width: '100%',
})

const ContentContainer = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100% - 64px)',
})

export default Quotas
